import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Inbox, FlaskConical, Pill, CheckCircle2, Loader2, PartyPopper, MessageSquare, Sparkles, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageHeader, SectionCard } from "@/components/Primitives";
import ComplianceShield from "@/components/ComplianceShield";
import AiAnswerCard from "@/components/AiAnswerCard";
import EHRCopyButton from "@/components/EHRCopyButton";

/**
 * End of Day Mode — "Inbox Zero" assistant.
 * Pulls pending portal messages, lab results, and refill requests.
 * For each item, ARIA drafts a response → physician reviews & approves with one tap.
 * Tracks pajama-time savings + sends "Done for today" confirmation.
 */
export default function EODMode() {
  const navigate = useNavigate();
  const [queue, setQueue] = useState({ messages: [], labs: [], refills: [], total_pending: 0 });
  const [savings, setSavings] = useState(null);
  const [drafts, setDrafts] = useState({}); // {item_id: {text, loading}}
  const [done, setDone] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    const [q, s] = await Promise.all([api.get("/eod/queue"), api.get("/eod/savings")]);
    setQueue(q.data);
    setSavings(s.data);
    if (q.data.total_pending === 0) setDone(true);
  };

  const draftItem = async (itemId) => {
    setDrafts(d => ({ ...d, [itemId]: { ...(d[itemId] || {}), loading: true, error: null } }));
    try {
      const r = await api.post("/eod/draft", { item_id: itemId });
      setDrafts(d => ({ ...d, [itemId]: { text: r.data.draft, loading: false, confidence: r.data.confidence || "moderate", error: null } }));
    } catch (err) {
      const msg = err.code === "ECONNABORTED"
        ? "ARIA took longer than 2 minutes. Try again — usually faster on retry."
        : err.response?.data?.detail || "ARIA draft failed. Tap retry to try again.";
      toast.error(msg);
      setDrafts(d => ({ ...d, [itemId]: { ...(d[itemId] || {}), loading: false, error: msg } }));
    }
  };

  const approveItem = async (itemId) => {
    try {
      await api.post("/eod/approve", { item_id: itemId, minutes_saved: 4 });
      toast.success("Approved — moving on");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not approve");
    }
  };

  const renderItem = (item) => {
    const draft = drafts[item.id];
    const isMsg = item.kind === "message";
    const isLab = item.kind === "lab";
    return (
      <div key={item.id} className="border border-brand rounded-lg p-4 bg-white" data-testid={`eod-item-${item.id}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-xs text-brand-muted">{item.data.patient}</div>
            {isMsg && (<>
              <div className="font-medium text-sm text-brand-text mt-1">{item.data.subject}</div>
              <div className="text-xs text-brand-muted mt-1 line-clamp-3">{item.data.body}</div>
            </>)}
            {isLab && (<>
              <div className="font-medium text-sm text-brand-text mt-1">{item.data.test}</div>
              <div className="text-xs text-brand-muted mt-1">Result: <b>{item.data.result}</b> — <span className={item.data.status === "normal" ? "text-emerald-700" : item.data.status === "improved" ? "text-emerald-700" : "text-amber-700"}>{item.data.status}</span></div>
            </>)}
            {!isMsg && !isLab && (<>
              <div className="font-medium text-sm text-brand-text mt-1">{item.data.medication}</div>
              <div className="text-xs text-brand-muted mt-1">{item.data.days_supply}-day supply · last visit {item.data.last_visit}</div>
            </>)}
          </div>
        </div>

        {draft?.text ? (
          <div className="mt-3 space-y-2">
            <div className="p-3 rounded-md bg-brand-bg border border-brand text-xs text-brand-text whitespace-pre-wrap" data-testid={`eod-draft-${item.id}`}>
              {draft.text}
            </div>
            <ComplianceShield
              outputId={item.id}
              module="eod"
              confidence={draft.confidence || "moderate"}
              summary={`EOD ${item.kind} draft for ${item.data.patient}`}
            />
            <div className="flex gap-2">
              <button
                onClick={() => approveItem(item.id)}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700"
                data-testid={`eod-approve-${item.id}`}
              >
                <CheckCircle2 className="w-3.5 h-3.5" /> Approve & send
              </button>
              <EHRCopyButton text={draft.text} module={`eod-${item.kind}`} label="Copy" />
            </div>
            <AiAnswerCard
              platformModule="eod"
              originalQuery={`${item.kind} draft for ${item.data.patient}`}
              originalAiResponse={draft.text}
              retrievedEntryIds={draft.retrieved_entry_ids || []}
              suggestedDomain="eod"
              testid={`eod-answer-card-${item.id}`}
            />
          </div>
        ) : (
          <>
            <button
              onClick={() => draftItem(item.id)}
              disabled={draft?.loading}
              className="mt-3 w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-md bg-brand-primary text-white text-xs font-medium hover:bg-[#35096B] disabled:opacity-50"
              data-testid={`eod-draft-btn-${item.id}`}
            >
              {draft?.loading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> ARIA drafting…</> : <><Sparkles className="w-3.5 h-3.5" /> Draft with ARIA</>}
            </button>
            {draft?.error && (
              <div className="mt-2 border border-red-200 bg-red-50 rounded-md p-2 flex items-start gap-2" data-testid={`eod-draft-error-${item.id}`}>
                <AlertCircle className="w-3.5 h-3.5 text-red-600 mt-0.5 shrink-0" />
                <div className="flex-1 text-[11px] text-red-900">{draft.error}</div>
                <button onClick={() => draftItem(item.id)} className="px-2 py-1 rounded bg-red-700 text-white text-[10px] font-medium" data-testid={`eod-draft-retry-${item.id}`}>Retry</button>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <>
      <PageHeader
        eyebrow="End of Day Mode"
        title="Clear your queue. Reclaim your evening."
        subtitle={savings ? `You've recovered ${savings.monthly_family_hours} family hours this month through ZidaMD.` : "Loading…"}
        testid="eod-header"
      />

      {/* Pajama Time Savings Meter */}
      {savings && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="border border-brand rounded-lg p-4 bg-white" data-testid="eod-savings-weekly">
            <div className="text-[10px] uppercase tracking-wide text-brand-muted">This week</div>
            <div className="text-3xl font-semibold text-brand-primary mt-1">{savings.weekly_hours}h</div>
            <div className="text-[11px] text-brand-muted mt-1">Pajama time eliminated</div>
          </div>
          <div className="border border-brand rounded-lg p-4 bg-white" data-testid="eod-savings-monthly">
            <div className="text-[10px] uppercase tracking-wide text-brand-muted">This month</div>
            <div className="text-3xl font-semibold text-brand-primary mt-1">{savings.monthly_hours}h</div>
            <div className="text-[11px] text-brand-muted mt-1">Total hours back</div>
          </div>
          <div className="border border-brand rounded-lg p-4 bg-white" data-testid="eod-savings-family">
            <div className="text-[10px] uppercase tracking-wide text-brand-muted">Family time</div>
            <div className="text-3xl font-semibold text-emerald-700 mt-1">{savings.monthly_family_hours}h</div>
            <div className="text-[11px] text-brand-muted mt-1">Personal hours protected</div>
          </div>
        </div>
      )}

      {done ? (
        <div className="border border-emerald-200 bg-emerald-50 rounded-lg p-8 text-center" data-testid="eod-done">
          <PartyPopper className="w-12 h-12 text-emerald-600 mx-auto mb-3" />
          <h2 className="text-xl font-semibold text-emerald-900">Done for today.</h2>
          <p className="text-sm text-emerald-800 mt-2">Inbox is clear. Go enjoy your evening — your family is waiting.</p>
          <button onClick={() => navigate("/dashboard")} className="mt-4 px-4 py-2 rounded-md bg-emerald-700 text-white text-sm font-medium hover:bg-emerald-800" data-testid="eod-back-dashboard">
            Back to dashboard
          </button>
        </div>
      ) : (
        <>
          <SectionCard title={`Portal messages (${queue.messages.length})`} testid="eod-messages-section">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">{queue.messages.map(renderItem)}</div>
          </SectionCard>

          <div className="h-6" />

          <SectionCard title={`Lab & imaging results (${queue.labs.length})`} testid="eod-labs-section">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">{queue.labs.map(renderItem)}</div>
          </SectionCard>

          <div className="h-6" />

          <SectionCard title={`Prescription refill requests (${queue.refills.length})`} testid="eod-refills-section">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">{queue.refills.map(renderItem)}</div>
          </SectionCard>
        </>
      )}
    </>
  );
}
